import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";

const CARDS = [
  { to: "/sectors/care-homes", id: "care", n: "01", t: "Care Home Contracts", d: "Resident-led, texture-modified menus with dignity at every meal.", img: "/images/care_dining.jpg", span: "md:col-span-8" },
  { to: "/sectors/schools", id: "schools", n: "02", t: "School Catering", d: "Lunches pupils actually finish.", img: "/images/school_lunch.jpg", span: "md:col-span-4" },
  { to: "/sectors/corporate", id: "corporate", n: "03", t: "Corporate Catering", d: "Staff restaurants, boardrooms & events.", img: "/images/corporate_modern_table.jpg", span: "md:col-span-5" },
  { to: "/flapjack", id: "flapjack", n: "04", t: "Flapjack Mix", d: "Our chef-made pancake & waffle mix. Shop online.", img: "/images/flapjack_blueberry_pancakes.jpg", logo: "/images/flapjack_logo.png", span: "md:col-span-7" },
  { to: "/chefs", id: "chefs", n: "05", t: "Chef Agency", d: "Cook on our projects across the UK. Apply with your CV in two minutes.", img: "/images/chef_team_service.jpg", span: "md:col-span-12" },
];

const Card = ({ c, i }) => {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <motion.div
      className={`col-span-1 ${c.span}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={c.to}
        onMouseMove={onMove}
        className="group relative block h-[360px] overflow-hidden rounded-[28px] bg-[#141412] md:h-full"
        data-testid={`bento-${c.id}-card`}
      >
        <img src={c.img} alt={c.t} className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-[1.4s] ease-out group-hover:scale-[1.06]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141412] via-[#141412]/40 to-transparent" />
        <div className="spot absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        <div className="relative flex h-full flex-col justify-between p-7 text-[#FAF8F5] sm:p-9">
          <div className="flex items-start justify-between">
            <span className="eyebrow rounded-full bg-[#141412]/70 px-3 py-1.5 text-[#C59B27] backdrop-blur">{c.n}</span>
            <span className="grid h-12 w-12 place-items-center rounded-full border border-white/25 transition-[transform,background-color] duration-500 group-hover:rotate-45 group-hover:bg-[#C59B27] group-hover:text-[#141412]">
              <ArrowUpRight className="h-5 w-5" />
            </span>
          </div>
          <div>
            {c.logo ? <img src={c.logo} alt="Flapjack" className="w-56 sm:w-72" /> : <h3 className="font-display text-3xl font-semibold sm:text-4xl">{c.t}</h3>}
            <p className="mt-2 max-w-md text-sm text-stone-300 sm:text-base">{c.d}</p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export const Bento = () => (
  <section id="sectors" className="bg-[#FAF8F5] py-24 sm:py-32" data-testid="sectors-bento">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="mb-14 grid gap-8 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <p className="eyebrow text-[#9a7614]">What we do</p>
          <MaskedLines lines={["One kitchen partner.", "Five ways in."]} italic={1} className="mt-4 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-6xl" />
        </div>
        <p className="text-base leading-relaxed text-stone-700 lg:col-span-5">
          Subscribe to a catering plan for your care home, school or workplace, join our agency chef network, or stock up on Flapjack. Every path starts here.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-5 md:auto-rows-[380px] md:grid-cols-12 lg:gap-6">
        {CARDS.map((c, i) => <Card key={c.id} c={c} i={i} />)}
      </div>
    </div>
  </section>
);
